import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, LabelList, ResponsiveContainer
} from 'recharts';

const SimulatorComparisonChart = ({ investorResults, params }) => {


  const getTermYears = (model, investorResult) => {
    const fallback = params?.termYears || 0;
    if (!investorResult || !model) return fallback;

    const rtoModels = [
      'Rent-to-Own',
      'Rent-to-Own + Gold',
      'Rent-to-Own + Stock',
      'Rent-to-Own + Real Estate'
    ];

    return rtoModels.includes(model)
      ? investorResult.termYears || fallback
      : fallback;
  };




  const formatShort = (value) => {
    if (typeof value !== 'number' || isNaN(value)) return '-';
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
    return value.toFixed(0);
  };

  const investedAmountDisplay = typeof params?.propertyValue === 'number' && params.propertyValue > 0
    ? `$${formatShort(params.propertyValue)}`
    : `your chosen amount`;

  const baseTermDisplay = typeof params?.termYears === 'number' && params.termYears > 0
    ? `${params.termYears}`
    : `a custom`;





  const formattedData = investorResults
    .filter(Boolean)
    .map((inv) => ({
      model: inv.model,
      roi: Number(inv.totalROI || 0),
      irr: Number(inv.irrEstimate || 0),
      exitValueRaw: Number(inv.exitValue || 0),
      exitValue: Number(inv.exitValue || 0) / 10000,
      termYears: getTermYears(inv.model, inv)
    }));


  const rtoModels = [
    'Rent-to-Own',
    'Rent-to-Own + Gold',
    'Rent-to-Own + Stock',
    'Rent-to-Own + Real Estate',
  ];

  const isRTO = (model) => rtoModels.includes(model);

  const validResults = investorResults.filter((r) => r && typeof r.exitValue !== 'undefined');

  // Avoid crash on missing data
  const termGroups = validResults.reduce(
    (acc, model) => {
      const term = isRTO(model.model)
        ? model.termYears ?? 0
        : params?.termYears ?? 0;

      if (isRTO(model.model)) {
        acc.rto.push(term);
      } else {
        acc.fixed.push(term);
      }

      return acc;
    },
    { rto: [], fixed: [] }
  );

  const rtoRange = {
    min: Math.min(...termGroups.rto),
    max: Math.max(...termGroups.rto),
  };

  const fixedTerm = params?.termYears ?? 0;


  return (
    <div className="w-full h-[500px] mb-6">
      <h2 className="text-2xl font-bold text-center mb-4 text-blue-800">
        Investment Strategy Comparison – {investedAmountDisplay} over {baseTermDisplay}-year base term
        <br />
        <span className="text-sm text-gray-500 font-medium">
          RTO models exited in {rtoRange.min === rtoRange.max
            ? `${rtoRange.max} years`
            : `${rtoRange.min}–${rtoRange.max} years`} based on performance
        </span>
      </h2>






      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={formattedData}
          margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
          barCategoryGap="20%"
          barGap={5}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="model"
            angle={-20}
            textAnchor="end"
            interval={0}
            height={60}
            tick={{ fontSize: 12 }}
          />
          <YAxis />
          <Tooltip
            formatter={(val, name) => {
              if (name === 'Exit Value ($)') {
                const value = typeof val === 'number' ? formatShort(val * 10000) : val;
                return [value, name];
              }
              const formatted = typeof val === 'number' ? val.toFixed(2) : val;
              return [formatted, name];
            }}
          />
          <Legend verticalAlign="top" height={36} />

          <Bar dataKey="roi" fill="#3b82f6" name="Total ROI (%)">
            <LabelList dataKey="roi" position="top" formatter={(val) => `${val.toFixed(0)}%`} style={{ fontSize: 8 }} />
          </Bar>

          <Bar dataKey="irr" fill="#10b981" name="IRR (%)">
            <LabelList dataKey="irr" position="top" formatter={(val) => `${val.toFixed(2)}%`} style={{ fontSize: 8 }} />
          </Bar>

          <Bar dataKey="exitValue" fill="#a855f7" name="Exit Value ($)">
            <LabelList
              dataKey="exitValueRaw"
              position="top"
              formatter={(val) => typeof val === 'number' ? formatShort(val) : ''}
              style={{ fontSize: 8 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SimulatorComparisonChart;
